import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { formatDate, formatMoney, formatNumber } from '@awj/i18n';
import { Skeleton } from '@awj/ui';

import { EmptyState } from '~/components/EmptyState.tsx';
import { ExportMenu } from '~/components/ExportMenu.tsx';
import { Icon } from '~/components/Icon.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { Pager } from '~/components/Pager.tsx';
import { Select } from '~/components/Select.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { initials } from '~/lib/initials.ts';
import { allCustomers, useCustomers } from '~/lib/customer-queries.ts';
import { customerColumns } from '~/lib/export-columns.ts';
import { useRowLink } from '~/lib/row-link.ts';

const SORTS = ['newest', 'name', 'spent', 'orders'] as const;

/**
 * Everyone who has an account.
 *
 * Staff are not here — they are managed under Settings, where the rules about not locking
 * yourself out live. Mixing them into this list is how somebody blocks a colleague.
 */
export function CustomersPage() {
  const { t, locale } = useI18n();
  const [params, setParams] = useSearchParams();

  const query = {
    search: params.get('q') ?? '',
    blocked: params.get('blocked') ?? '',
    hasOrdered: params.get('ordered') ?? '',
    sort: params.get('sort') ?? '',
    page: Number(params.get('page')) || 1,
  };

  const customers = useCustomers(query);
  const rowLink = useRowLink();

  const [search, setSearch] = useState(query.search);
  useEffect(() => setSearch(query.search), [query.search]);

  /*
   * The tick, held locally as well as in the URL.
   *
   * The box is driven by the query string, and the query string does not move until the router
   * has been round. Rendering straight from it meant a click put the tick on and React took it
   * straight back off, then on again a moment later when the URL caught up — which reads as a
   * dropped click. This shows the intent at once and follows the URL whenever it changes for
   * some other reason, the same arrangement the search box above already uses.
   */
  const [hasOrdered, setHasOrdered] = useState(query.hasOrdered === 'true');
  useEffect(() => setHasOrdered(query.hasOrdered === 'true'), [query.hasOrdered]);

  useEffect(() => {
    if (search === query.search) {
      return;
    }

    const timer = setTimeout(() => update({ q: search }), 350);

    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  function update(changes: Record<string, string>) {
    const next = new URLSearchParams(params);

    for (const [key, value] of Object.entries(changes)) {
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
    }

    if (!('page' in changes)) {
      next.delete('page');
    }

    setParams(next, { replace: true });
  }

  return (
    <>
      <PageTitle title={t('nav.customers')} />

      <div className="filter-bar">
        <div className="filter-bar__row">
          <label className="filter-bar__search">
            <span className="sr-only">{t('customers.search')}</span>
            <span className="filter-bar__search-icon">
              <Icon name="search" size={16} />
            </span>
            <input
              className="filter-bar__search-input"
              type="search"
              value={search}
              placeholder={t('customers.search')}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <div className="filter-bar__controls">
            <div className="filter-bar__field">
              <label htmlFor="filter-account">{t('customers.account')}</label>
              <Select
                id="filter-account"
                value={query.blocked}
                onChange={(blocked) => update({ blocked })}
                placeholder={t('customers.allAccounts')}
                options={[
                  { value: 'false', label: t('customers.activeOnly') },
                  { value: 'true', label: t('customers.blockedOnly') },
                ]}
              />
            </div>

            <div className="filter-bar__field">
              <label htmlFor="filter-sort">{t('customers.sort')}</label>
              <Select
                id="filter-sort"
                value={query.sort}
                onChange={(sort) => update({ sort })}
                options={SORTS.map((sort) => ({
                  value: sort === 'newest' ? '' : sort,
                  label: t(`customerSort.${sort}` as 'customerSort.newest'),
                }))}
              />
            </div>

            <label className="checkbox filter-bar__toggle">
              <input
                type="checkbox"
                checked={hasOrdered}
                onChange={(event) => {
                  setHasOrdered(event.target.checked);
                  update({ ordered: event.target.checked ? 'true' : '' });
                }}
              />
              <span>{t('customers.hasOrdered')}</span>
            </label>
          </div>

          <div className="filter-bar__actions">
            <ExportMenu
              file="customers"
              sheet={t('nav.customers')}
              columns={customerColumns(t, locale)}
              rows={() => allCustomers(query)}
              hint={t('export.allMatching')}
              disabled={!customers.data?.total}
            />
          </div>
        </div>
      </div>

      {customers.isPending ? (
        <Skeleton height="320px" />
      ) : customers.isError ? (
        <EmptyState
          icon="alert"
          title={t('common.somethingWentWrong')}
          hint={t('common.somethingWentWrongHint')}
          action={
            <button type="button" className="awj-button awj-button--secondary" onClick={() => customers.refetch()}>
              {t('common.retry')}
            </button>
          }
        />
      ) : customers.data.items.length === 0 ? (
        <EmptyState icon="people" title={t('customers.none')} hint={t('customers.noneHint')} tall />
      ) : (
        <div className="data-table">
            <div className="data-table__scroll" tabIndex={0} role="region" aria-label={t('nav.customers')}>
              <table className="data-table__table">
                <thead>
                  <tr>
                    <th className="data-table__th" scope="col">{t('customers.customer')}</th>
                    <th className="data-table__th" scope="col">{t('customers.registered')}</th>
                    <th className="data-table__th" scope="col">{t('customers.lastOrder')}</th>
                    <th scope="col" className="data-table__th data-table__cell--end">
                      {t('customers.orders')}
                    </th>
                    <th scope="col" className="data-table__th data-table__cell--end">
                      {t('customers.spent')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {customers.data.items.map((customer) => (
                    <tr key={customer.id} {...rowLink(`/customers/${customer.id}`)}>
                      <td className="data-table__td">
                        <span className="cell-identity">
                          <span className="cell-avatar" aria-hidden="true">
                            {initials(customer.fullName, customer.email)}
                          </span>
                          <span className="cell-identity__text">
                            <Link className="data-table__link" to={`/customers/${customer.id}`}>
                              <bdi>{customer.fullName || customer.email}</bdi>
                            </Link>
                            <span className="data-table__sub">
                              <bdi>{customer.email}</bdi>
                            </span>
                            {/*
                              Two different facts, and both matter to whoever is looking: an account
                              that was stopped, and one that never finished registering.
                            */}
                            {customer.isBlocked ? (
                              <span className="status status--cancelled">{t('customers.blocked')}</span>
                            ) : !customer.emailConfirmed ? (
                              <span className="status status--draft">{t('customers.unconfirmed')}</span>
                            ) : null}
                          </span>
                        </span>
                      </td>
                      <td className="data-table__td">
                        <bdi>{formatDate(locale, customer.registeredAt)}</bdi>
                      </td>
                      <td className="data-table__td">
                        {customer.lastOrderAt ? (
                          <bdi>{formatDate(locale, customer.lastOrderAt)}</bdi>
                        ) : (
                          <span className="data-table__sub">{t('customers.neverOrdered')}</span>
                        )}
                      </td>
                      <td className="data-table__td data-table__cell--end">
                        <bdi>{formatNumber(locale, customer.orderCount)}</bdi>
                      </td>
                      <td className="data-table__td data-table__cell--end">
                        <bdi>{formatMoney(locale, customer.totalSpent)}</bdi>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pager
              page={customers.data.page}
              totalPages={customers.data.totalPages ?? 1}
              total={customers.data.total}
              onPage={(page) => update({ page: String(page) })}
            />
        </div>
      )}
    </>
  );
}
